import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useRouter } from "../app/Router";
import { useAuth } from "../hooks/useAuth";
import { useTheme } from "../hooks/useTheme";
import { useGoogleSyncStore } from "../stores/googleSyncStore";
import { searchEvents, type SearchEventResult } from "../lib/api";

interface CommandAction {
  id: string;
  label: string;
  shortcut?: string;
  action: () => void;
}

interface CommandPaletteProps {
  onNewEvent?: () => void;
}

const SEARCH_DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 2;

function formatDate(d: { year: number; month: number; day: number }, locale?: string): string {
  return new Date(d.year, d.month - 1, d.day).toLocaleDateString(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function CommandPalette({ onNewEvent }: CommandPaletteProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [results, setResults] = useState<SearchEventResult[]>([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { navigate } = useRouter();
  const { logout } = useAuth();
  const { theme, cycle } = useTheme();
  const failingCount = useGoogleSyncStore(
    (s) => s.syncSources.filter((src) => src.lastError).length,
  );

  const themeLabel = theme === "dark" ? "Dark" : theme === "light" ? "Light" : "Auto";

  const actions: CommandAction[] = useMemo(
    () => [
      {
        id: "new-event",
        label: "New event",
        action: () => {
          onNewEvent?.();
        },
      },
      ...(failingCount > 0
        ? [
            {
              id: "sync-issues",
              label: `Sync issues (${failingCount}) — open Settings`,
              action: () => navigate("/settings"),
            },
          ]
        : []),
      {
        id: "settings",
        label: "Settings",
        action: () => navigate("/settings"),
      },
      {
        id: "theme",
        label: `Theme: ${themeLabel}`,
        action: () => cycle(),
      },
      {
        id: "today",
        label: "Go to today",
        action: () => {
          // Dispatch a custom event that CalendarPage can listen to
          window.dispatchEvent(new CustomEvent("calendar:today"));
        },
      },
      {
        id: "signout",
        label: "Sign out",
        action: () => logout(),
      },
    ],
    [onNewEvent, navigate, logout, cycle, themeLabel, failingCount],
  );

  const filteredActions = useMemo(() => {
    if (!query.trim()) return actions;
    const q = query.toLowerCase();
    return actions.filter((a) => a.label.toLowerCase().includes(q));
  }, [query, actions]);

  // Debounced event search
  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setSearching(false);
      return;
    }
    const controller = new AbortController();
    setSearching(true);
    const handle = setTimeout(() => {
      searchEvents(q, controller.signal)
        .then((found) => {
          setResults(found);
          setSearching(false);
        })
        .catch((err) => {
          if (err?.name !== "AbortError") setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      controller.abort();
      clearTimeout(handle);
    };
  }, [query]);

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      setResults([]);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Global keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const runAction = useCallback((action: CommandAction) => {
    setOpen(false);
    action.action();
  }, []);

  const goToEvent = useCallback((ev: SearchEventResult) => {
    setOpen(false);
    window.dispatchEvent(
      new CustomEvent("calendar:navigate-to", { detail: ev.date.start }),
    );
  }, []);

  // Unified list: actions first, then event results.
  const totalCount = filteredActions.length + results.length;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (totalCount === 0 ? 0 : (i + 1) % totalCount));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => (totalCount === 0 ? 0 : (i - 1 + totalCount) % totalCount));
      } else if (e.key === "Enter" && totalCount > 0) {
        e.preventDefault();
        if (activeIndex < filteredActions.length) {
          const action = filteredActions[activeIndex];
          if (action) runAction(action);
        } else {
          const ev = results[activeIndex - filteredActions.length];
          if (ev) goToEvent(ev);
        }
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    },
    [filteredActions, results, activeIndex, totalCount, runAction, goToEvent],
  );

  // Clamp activeIndex when the list shrinks
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, totalCount - 1)));
  }, [totalCount]);

  if (!open) return null;

  return (
    <div className="cmd-overlay" onClick={() => setOpen(false)}>
      <div className="cmd-palette" onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <input
          ref={inputRef}
          className="cmd-input"
          placeholder="Search events or run a command..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          spellCheck={false}
          autoComplete="off"
        />
        <div className="cmd-list" role="listbox">
          {totalCount === 0 && (
            <div className="cmd-empty">
              {query.trim().length >= MIN_QUERY_LENGTH && !searching
                ? "No matches"
                : query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH
                ? `Type ${MIN_QUERY_LENGTH - query.trim().length} more character${
                    MIN_QUERY_LENGTH - query.trim().length === 1 ? "" : "s"
                  } to search events`
                : "No matching commands"}
            </div>
          )}
          {filteredActions.length > 0 && (
            <div className="cmd-section-label">Commands</div>
          )}
          {filteredActions.map((action, i) => (
            <button
              key={action.id}
              className={`cmd-item ${i === activeIndex ? "active" : ""}`}
              role="option"
              aria-selected={i === activeIndex}
              onClick={() => runAction(action)}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <span>{action.label}</span>
              {action.shortcut && <kbd className="cmd-kbd">{action.shortcut}</kbd>}
            </button>
          ))}
          {results.length > 0 && (
            <div className="cmd-section-label">
              Events{searching ? " · searching..." : ""}
            </div>
          )}
          {results.map((ev, i) => {
            const overallIndex = filteredActions.length + i;
            return (
              <button
                key={ev.id}
                className={`cmd-item cmd-item-event ${overallIndex === activeIndex ? "active" : ""}`}
                role="option"
                aria-selected={overallIndex === activeIndex}
                onClick={() => goToEvent(ev)}
                onMouseEnter={() => setActiveIndex(overallIndex)}
              >
                <span
                  className="cmd-event-dot"
                  style={{ background: ev.color || ev.calendarColor }}
                  aria-hidden
                />
                <span className="cmd-event-body">
                  <span className="cmd-event-heading">{ev.heading}</span>
                  <span className="cmd-event-meta">
                    {formatDate(ev.date.start)} · {ev.calendarName}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        <div className="cmd-footer">
          <kbd className="cmd-kbd-hint">↑↓</kbd>
          <span>navigate</span>
          <kbd className="cmd-kbd-hint">↵</kbd>
          <span>select</span>
          <kbd className="cmd-kbd-hint">esc</kbd>
          <span>close</span>
        </div>
      </div>
    </div>
  );
}
